import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, TrendingUp, MousePointer, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CornerstoneConversionCard
 * --------------------------
 * Admin-only card that visualises which cornerstone blog post is actually
 * converting clicks into demo signups (`cornerstone_demo_cta` events).
 *
 * Shows: conversion rate per post, the winner, and the totals.
 * Pulls from /api/admin/blog/cornerstone-conversion (admin-gated).
 */
const CornerstoneConversionCard = ({ days = 30 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/admin/blog/cornerstone-conversion?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(r.data);
      } catch (_e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  if (loading) {
    return (
      <Card data-testid="cornerstone-conversion-card" className="bg-slate-900 border-slate-800">
        <CardContent className="p-6 flex items-center justify-center text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          Loading cornerstone conversion…
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.posts || data.posts.length === 0) {
    return (
      <Card data-testid="cornerstone-conversion-card" className="bg-slate-900 border-slate-800">
        <CardContent className="p-6 text-center text-slate-400 text-sm">
          No cornerstone-click data yet. Once readers click your cornerstone deep dives,
          conversion analytics will appear here.
        </CardContent>
      </Card>
    );
  }

  const { totals, posts, winner_slug, window_days } = data;

  return (
    <Card
      data-testid="cornerstone-conversion-card"
      className="bg-slate-900 bg-gradient-to-br from-slate-900 via-slate-900 to-amber-950/40 border border-amber-500/30 shadow-[0_0_40px_-12px_rgba(251,191,36,0.25)]"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500/30 to-amber-600/10 border border-amber-400/40 flex items-center justify-center shadow-inner shadow-amber-500/20">
              <Trophy className="w-4 h-4 text-amber-200" />
            </div>
            <div>
              <CardTitle className="text-base text-white tracking-tight">
                Top-Performing Cornerstone
              </CardTitle>
              <p className="text-[11px] text-amber-300/70 mt-0.5">
                Click → demo CTA conversion across your deep dives
              </p>
            </div>
          </div>
          <Badge className="bg-amber-400/15 text-amber-200 border-amber-400/40 text-[11px] tracking-widest font-semibold px-2.5 py-1">
            LAST {window_days}D
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Totals row — vibrant tinted tiles */}
        <div className="grid grid-cols-3 gap-2.5" data-testid="cornerstone-totals">
          <Tile
            icon={MousePointer}
            label="Total clicks"
            value={totals.clicks}
            tint="cyan"
            testid="totals-clicks"
          />
          <Tile
            icon={TrendingUp}
            label="Demo CTAs"
            value={totals.demo_ctas}
            tint="emerald"
            testid="totals-demo-ctas"
          />
          <Tile
            icon={Trophy}
            label="Avg conversion"
            value={`${totals.rate_pct}%`}
            tint="amber"
            testid="totals-rate"
          />
        </div>

        {/* Per-post leaderboard — colour-tinted rows + animated winner */}
        <div className="space-y-2 pt-2 border-t border-slate-800/60" data-testid="cornerstone-leaderboard">
          {posts.map((p) => (
            <div
              key={p.slug}
              data-testid={`conversion-row-${p.slug}`}
              className={`relative p-3 rounded-lg border transition-all overflow-hidden ${
                p.is_winner
                  ? 'bg-gradient-to-r from-amber-500/[0.12] via-amber-500/[0.06] to-transparent border-amber-400/40 shadow-md shadow-amber-500/10'
                  : 'bg-slate-900 border-slate-800/60 hover:border-slate-700'
              }`}
            >
              {p.is_winner && (
                <div className="absolute inset-y-0 left-0 w-0.5 bg-amber-300 bg-gradient-to-b from-amber-300 via-amber-400 to-amber-500" />
              )}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className={`text-[11px] font-bold tracking-wider w-5 ${
                    p.is_winner ? 'text-amber-200' : 'text-slate-400'
                  }`}>
                    #{p.rank}
                  </span>
                  <span className={`text-sm truncate ${p.is_winner ? 'text-white font-semibold' : 'text-slate-200'}`}>
                    {p.title}
                  </span>
                  {p.is_winner && (
                    <Badge className="bg-amber-400 text-slate-950 text-[11px] font-bold tracking-widest shadow-md shadow-amber-500/40">
                      🏆 WINNER
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs tabular-nums">
                  <span className="text-slate-400">
                    <span className="text-cyan-300 font-semibold">{p.clicks}</span> clicks
                  </span>
                  <span className="text-slate-400">
                    <span className="text-emerald-300 font-semibold">{p.demo_ctas}</span> demos
                  </span>
                  <span className={`font-bold tabular-nums ${
                    p.rate_pct >= 40 ? 'text-emerald-300' :
                    p.rate_pct >= 20 ? 'text-amber-300' :
                    'text-rose-300'
                  }`}>
                    {p.rate_pct}%
                  </span>
                </div>
              </div>
              {/* Conversion bar — gradient fill, glow on winner */}
              <div className="mt-2 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    p.is_winner
                      ? 'bg-amber-300 bg-gradient-to-r from-amber-300 to-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.6)]' :
                    p.rate_pct >= 40 ? 'bg-emerald-400 bg-gradient-to-r from-emerald-400 to-emerald-300' :
                    p.rate_pct >= 20 ? 'bg-amber-500 bg-gradient-to-r from-amber-500 to-amber-400' :
                    'bg-rose-500 bg-gradient-to-r from-rose-500 to-rose-400'
                  }`}
                  style={{ width: `${Math.min(p.rate_pct, 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>

        {winner_slug && (
          <div className="rounded-lg bg-gradient-to-r from-slate-800/60 to-amber-950/30 border border-amber-500/20 p-3 text-xs text-slate-300 leading-relaxed">
            💡 <span className="text-amber-200 font-semibold">Insight:</span> Your highest-converting
            cornerstone is driving <span className="text-amber-300 font-bold">
              {posts.find((p) => p.slug === winner_slug)?.rate_pct}%
            </span> of click-throughs into demo signups —
            <span className="text-slate-400"> consider commissioning more deep dives in this category.</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Tile = ({ icon: Icon, label, value, tint, testid }) => {
  // Vibrant accent palette — saturated for the icon/label, soft tint for the tile bg
  const tints = {
    cyan:    { color: '#0891b2', glow: 'rgba(8,145,178,0.30)',  bg: 'rgba(8,145,178,0.08)',  border: 'rgba(8,145,178,0.30)' },
    emerald: { color: '#059669', glow: 'rgba(5,150,105,0.30)',  bg: 'rgba(5,150,105,0.08)',  border: 'rgba(5,150,105,0.30)' },
    amber:   { color: '#b45309', glow: 'rgba(180,83,9,0.30)',   bg: 'rgba(180,83,9,0.08)',   border: 'rgba(180,83,9,0.30)' },
  };
  const t = tints[tint] || tints.cyan;
  return (
    <div
      data-testid={testid}
      className="relative p-3 rounded-lg overflow-hidden border transition-all hover:scale-[1.02] hover:shadow-lg group bg-white"
      style={{
        borderColor: t.border,
        boxShadow: `0 1px 0 0 rgba(255,255,255,0.5) inset, 0 4px 12px -4px ${t.glow}`,
      }}
    >
      <div
        className="absolute inset-x-0 top-0 h-0.5 transition-all group-hover:h-1"
        style={{ background: `linear-gradient(90deg, ${t.color}, transparent)` }}
      />
      <div
        className="w-7 h-7 rounded-md border flex items-center justify-center mb-2"
        style={{ backgroundColor: t.bg, borderColor: t.border, color: t.color }}
      >
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="text-lg font-bold text-slate-900 leading-none mb-1 tabular-nums">{value}</div>
      <div
        className="text-[11px] font-semibold tracking-wide uppercase"
        style={{ color: t.color }}
      >
        {label}
      </div>
    </div>
  );
};

export default CornerstoneConversionCard;
